import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Workflow, Code, Webhook, Package } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const CodeWatermark = () => {
  const [lines, setLines] = useState([]);

  useEffect(() => {
    const generateCode = () => {
      const codeSnippets = [
        'async function processTransaction(data) {',
        '  const validated = await schema.validate(data);',
        '  return pipeline.execute(validated);',
        '}',
        '',
        'export class DataStream {',
        '  constructor(config) {',
        '    this.buffer = new CircularBuffer(1e6);',
        '    this.throughput = 1_200_000;',
        '  }',
        '',
        '  async *stream() {',
        '    while (this.active) {',
        '      yield await this.buffer.next();',
        '    }',
        '  }',
        '}',
        '',
        'const API_ENDPOINT = "/api/v2/enterprise";',
        'const TPS_LIMIT = 1_200_000;',
        'const LATENCY_TARGET = 10; // ms',
        '',
        'interface TransactionPayload {',
        '  id: string;',
        '  timestamp: number;',
        '  data: Record<string, unknown>;',
        '  signature: string;',
        '}',
        '',
        '// Real-time data processing pipeline',
        'pipeline.on("data", async (chunk) => {',
        '  const processed = transform(chunk);',
        '  await sink.write(processed);',
        '});'
      ];
      setLines(codeSnippets);
    };
    generateCode();
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden opacity-[0.03] pointer-events-none select-none">
      <pre 
        className="text-xs leading-relaxed text-white whitespace-pre-wrap p-8"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {lines.map((line, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.05 }}
          >
            {line}
          </motion.div>
        ))}
      </pre>
    </div>
  );
};

const TPSCounter = () => {
  const [count, setCount] = useState(1200000);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => {
        const delta = Math.floor(Math.random() * 10000) - 5000;
        return Math.max(1150000, Math.min(1250000, prev + delta));
      });
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="font-mono tabular-nums">
      {count.toLocaleString()}
    </span>
  );
};

const EcosystemSection = () => {
  const { t } = useLanguage();

  const features = [
    { icon: Code, key: 'rest' },
    { icon: Workflow, key: 'graphql' },
    { icon: Webhook, key: 'webhooks' },
    { icon: Package, key: 'sdk' }
  ];

  return (
    <section 
      id="ecosystem" 
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="ecosystem-section"
    >
      {/* Background Circuit Pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            radial-gradient(circle at 25% 25%, rgba(0, 240, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(0, 240, 255, 0.1) 0%, transparent 50%),
            linear-gradient(rgba(0, 240, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 240, 255, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '100% 100%, 100% 100%, 40px 40px, 40px 40px'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/98 to-black" />

      {/* Code Watermark */}
      <CodeWatermark />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <motion.span 
            className="text-[#00F0FF] font-mono text-sm tracking-[0.3em] uppercase"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {t('ecosystem.subtitle')}
          </motion.span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 tracking-tight">
            {t('ecosystem.title')}
          </h2>
          <p className="text-[#A3A3A3] text-lg mt-6 max-w-2xl leading-relaxed">
            {t('ecosystem.description')}
          </p>
        </motion.div>

        {/* TPS Display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 p-8 md:p-12 bg-[#050505] border border-white/10 rounded-sm text-center"
        >
          <div className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-4">
            <TPSCounter />
          </div>
          <div className="text-[#00F0FF] font-mono text-sm md:text-base tracking-[0.2em] uppercase">
            {t('ecosystem.tpsLabel')}
          </div>
          
          {/* Animated Line */}
          <div className="mt-8 h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#00F0FF]"
              initial={{ width: '0%' }}
              animate={{ width: ['0%', '100%', '0%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {features.map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.05, borderColor: 'rgba(0, 240, 255, 0.5)' }}
              className="p-6 bg-[#050505] border border-white/10 rounded-sm transition-all duration-300 text-center"
            >
              <feature.icon className="w-8 h-8 text-[#00F0FF] mx-auto mb-4" />
              <span className="text-white font-medium">{t(`ecosystem.features.${feature.key}`)}</span>
            </motion.div>
          ))}
        </div>

        {/* API Code Block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-12 p-6 bg-[#050505] border border-white/10 rounded-sm overflow-hidden"
          data-testid="api-code-block"
        >
          <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
            <div className="w-3 h-3 rounded-full bg-[#FF003C]" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="ml-4 text-sm text-[#A3A3A3] font-mono">api-example.js</span>
          </div>
          <pre 
            className="text-sm text-[#A3A3A3] overflow-x-auto"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <code>{`import { PapesClient } from '@papes/sdk';

const client = new PapesClient({
  apiKey: process.env.PAPES_API_KEY,
  environment: 'production'
});

// Stream real-time predictions
const stream = client.predictions.stream({
  model: 'financial-v3',
  throughput: 1_200_000
});

stream.on('data', (prediction) => {
  console.log('Prediction:', prediction);
});`}</code>
          </pre>
        </motion.div>
      </div>
    </section>
  );
};

export default EcosystemSection;
